import {
  entries,
  filter,
  flatMap,
  isIncludedIn,
  map,
  pipe,
  uniqueBy,
} from '@vestigas/utils';

import { type AssetChain } from '../../useQueryDeliveryNoteChainsAdmin';
import {
  type Attachment,
  type AttachmentEnhanced,
  type AttachmentMimeType,
} from '../types';

import { getCreatorId } from './getCreatorId';

/**
 * Get the (correct) MIME type of an attachment.
 * @param attachment - The attachment to get the content type of.
 * @returns The content type of the attachment.
 */
const getContentMimeType = (
  attachment: Attachment,
): AttachmentMimeType | undefined => {
  const contentType = attachment?.contentType ?? attachment?.comment; // accept item attachments store the type in a comment

  if (contentType === 'application/octet-stream') {
    // octet-stream could be literally anything, but we know it's a PDF,
    // so let's map the MIME type to the more appropriate one
    return 'application/pdf';
  }

  return contentType;
};

/**
 * Gets all attachments from all asset chains.
 * @param assetChains - The asset chains to get the attachments from.
 * @returns The list of attachments.
 */
export const getAttachments = (assetChains: AssetChain[]) => {
  if (!assetChains?.length) {
    return [];
  }

  const processGeneralAttachments = (
    chain: AssetChain,
  ): AttachmentEnhanced[] => {
    const { attachments = {}, id } = chain;
    const creatorId = getCreatorId(chain.createdBy);
    const chainCreatedOn = chain?.createdOn;

    return pipe(
      attachments,
      entries(),
      map(([path, attachment]) => ({
        ...attachment,
        attachmentType: 'delivery' as const,
        chainCreatedOn,
        chainId: id,
        contentType: getContentMimeType(attachment),
        creatorId,
        id: attachment?.digest,
        path: pipe(
          path.split('/'),
          filter((pathItem) => !isIncludedIn(pathItem, ['blob_', 'blob'])),
        ),
      })),
    );
  };

  const processLegacyAttachments = (
    chain: AssetChain,
  ): AttachmentEnhanced[] => {
    const { id, meta } = chain;
    const creatorId = getCreatorId(chain.createdBy);
    const chainCreatedOn = chain?.createdOn;

    if (!meta?.acceptEvent?.acceptedItems?.length) {
      return [];
    }

    return pipe(
      meta.acceptEvent.acceptedItems,
      filter((acceptedItem) => Boolean(acceptedItem.attachments)),
      flatMap((acceptedItem) =>
        map(acceptedItem.attachments, (acceptAttachment) => {
          const acceptAttachmentValue = acceptAttachment.blob;
          return {
            ...acceptAttachment,
            attachmentType: 'delivery' as const,
            chainCreatedOn,
            chainId: id,
            contentType: getContentMimeType(acceptAttachmentValue),
            creatorId,
            id: acceptAttachmentValue?.digest,
            path: acceptedItem.path,
          };
        }),
      ),
    );
  };

  return pipe(
    assetChains,
    flatMap((chain) => [
      ...processGeneralAttachments(chain),
      ...processLegacyAttachments(chain),
    ]),
    uniqueBy(({ id }) => id),
  );
};
